import { useEffect, useRef } from 'react';
import { trackEventOnce } from '../utils/analytics';

const DEPTH_THRESHOLDS = [25, 50, 75, 90];
const SECTION_MIN_VISIBLE_MS = 750;

export function useReportAnalytics({
  rootRef,
  analysisType,
  resultKey,
  enabled = true,
}) {
  const trackedSectionsRef = useRef(new Set());
  const trackedDepthsRef = useRef(new Set());

  useEffect(() => {
    trackedSectionsRef.current = new Set();
    trackedDepthsRef.current = new Set();
  }, [resultKey]);

  useEffect(() => {
    if (!enabled || !rootRef.current || typeof IntersectionObserver === 'undefined') return undefined;

    const sections = rootRef.current.querySelectorAll('[data-analytics-section]');
    if (!sections.length) return undefined;

    const timers = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const sectionName = entry.target.dataset.analyticsSection;
        if (!sectionName || trackedSectionsRef.current.has(sectionName)) return;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          window.clearTimeout(timers.get(entry.target));
          timers.delete(entry.target);
          return;
        }
        if (timers.has(entry.target)) return;
        const timerId = window.setTimeout(() => {
          timers.delete(entry.target);
          trackedSectionsRef.current.add(sectionName);
          trackEventOnce('report_section_viewed', `${analysisType}:${resultKey}:${sectionName}`, {
            analysis_type: analysisType,
            section_name: sectionName,
          });
        }, SECTION_MIN_VISIBLE_MS);
        timers.set(entry.target, timerId);
      });
    }, { threshold: [0.5] });

    sections.forEach((section) => observer.observe(section));
    return () => {
      observer.disconnect();
      timers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [analysisType, enabled, resultKey, rootRef]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const checkDepth = () => {
      const documentHeight = document.documentElement.scrollHeight;
      const scrollableHeight = documentHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      const depth = Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100));
      DEPTH_THRESHOLDS.forEach((threshold) => {
        if (depth < threshold || trackedDepthsRef.current.has(threshold)) return;
        trackedDepthsRef.current.add(threshold);
        trackEventOnce('report_depth_reached', `${analysisType}:${resultKey}:${threshold}`, {
          analysis_type: analysisType,
          depth_percent: threshold,
        });
      });
    };

    const frameId = window.requestAnimationFrame(checkDepth);
    window.addEventListener('scroll', checkDepth, { passive: true });
    window.addEventListener('resize', checkDepth);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', checkDepth);
      window.removeEventListener('resize', checkDepth);
    };
  }, [analysisType, enabled, resultKey]);
}
